import { AxiosError } from 'axios';
import { useCallback, useEffect, useState } from 'react';

import { useVoteFarm } from '@services/api/voting/common.api';

import { useAuth } from '@contexts/common/AuthorizationContext';

import useDebounce from '@hooks/common/useDebounce';

import { parseAxiosError } from '@helpers/common/error.helpers';

import { DEFAULT_DELAY } from '@constants/common/common.constants';

import { FarmDataDto } from '@dto/vaults/voting.dto';

interface Params {
  link: string;
  isValid: boolean;
}

export const useVotingFarmAction = ({ link, isValid }: Params) => {
  const { account } = useAuth();

  const [farmData, setFarmData] = useState<FarmDataDto | null>(null);

  const debouncedLink = useDebounce(link, {
    delay: DEFAULT_DELAY,
  });

  const { mutateAsync, isPending, error } = useVoteFarm();

  const voteFarm = useCallback(
    async (linkStr: string) => {
      try {
        if (!account || !linkStr || !isValid) return;
        const params = { link: linkStr.trim(), account };
        const res = await mutateAsync(params);
        setFarmData(res);
      } catch (e) {
        console.error('useVotingFarmAction', e);
        setFarmData(null);
      }
    },
    [account, mutateAsync, isValid],
  );

  useEffect(() => {
    if (debouncedLink) {
      (async () => {
        await voteFarm(debouncedLink);
      })();
    } else {
      setFarmData(null);
    }
  }, [debouncedLink]);

  return {
    isPending,
    farmData,
    voteFarm,
    error: error && parseAxiosError(error as AxiosError),
  };
};
